import React, { useState, useEffect, useRef } from 'react';
import { Mail, Send, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Dropdown from '../../components/Dropdown';

const SendEmail = () => {
  const [to, setTo] = useState('');
  const [template, setTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [dbTemplates, setDbTemplates] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachedFiles(e.target.files);
    }
  };

  const removeFiles = () => {
    setAttachedFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get('/masters/email-templates');
        if (res.data && Array.isArray(res.data)) {
          setDbTemplates(res.data.filter(t => t.status === 'Active'));
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      }
    };
    fetchTemplates();
  }, []);
  
  const handleTemplateChange = (val) => {
    setTemplate(val);
    
    if (val === '') {
      setSubject('');
      setMessage('');
      return;
    }

    const selectedTemplate = dbTemplates.find(t => (t.id || t._id) == val);
    if (selectedTemplate) {
      setSubject(selectedTemplate.subject || '');
      // strip html tags from body or just set it
      let textBody = selectedTemplate.body || '';
      // Very basic html to text for textarea
      textBody = textBody.replace(/<[^>]*>?/gm, '');
      setMessage(textBody);
    }
  };

  const handleSend = async () => {
    if (!to || !subject || !message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Create FormData since we have files
    const formData = new FormData();
    formData.append('to', to);
    formData.append('subject', subject);
    formData.append('message', message);
    
    if (attachedFiles) {
      for (let i = 0; i < attachedFiles.length; i++) {
        formData.append('attachments', attachedFiles[i]);
      }
    }

    try {
      const res = await api.post('/email/send-email', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data && res.data.success) {
        toast.success('Email successfully sent to ' + to);
        // clear form
        setTo('');
        setTemplate('');
        setSubject('');
        setMessage('');
        removeFiles();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to send email. Ensure the email-server.js is running and SMTP is correct.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-main">Send Email</h1>
          <p className="text-muted mt-1">Compose and send emails to leads directly from the CRM.</p>
        </div>
      </div>

      <div className="card max-w-4xl">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-6">
          <div style={{ backgroundColor: 'var(--color-primary-light)', padding: '0.75rem', borderRadius: '12px' }}>
            <Mail size={24} className="text-primary" />
          </div>
          <h2 className="text-xl font-bold">Compose New Message</h2>
        </div>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-semibold mb-2 block text-slate-700">To Email Address <span className="text-danger">*</span></label>
              <input 
                type="email" 
                className="input-field w-full" 
                placeholder="recipient@example.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold mb-2 block text-slate-700">Select Template (Optional)</label>
              <Dropdown 
                style={{ width: '100%' }}
                value={template} 
                onChange={handleTemplateChange}
                placeholder="-- No Template (Custom Message) --"
                options={[
                  { value: '', label: '-- No Template (Custom Message) --' },
                  ...dbTemplates.map(t => ({ value: t.id || t._id, label: t.name }))
                ]}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold mb-2 block text-slate-700">Subject <span className="text-danger">*</span></label>
            <input 
              type="text" 
              className="input-field w-full" 
              placeholder="Enter email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-semibold mb-2 block text-slate-700">Message <span className="text-danger">*</span></label>
            <textarea 
              className="input-field w-full" 
              rows="12" 
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ fontFamily: 'inherit', resize: 'vertical' }}
            ></textarea>
          </div>

          <div className="flex justify-between items-center mt-2 pt-4 border-t border-slate-100 w-full">
            <div className="flex items-center gap-3">
              <input 
                type="file" 
                multiple 
                className="hidden" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                style={{ display: 'none' }}
              />
              <button 
                className="text-muted hover:text-main flex items-center gap-2 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                <Paperclip size={18} /> Attach Files
              </button>
              
              {attachedFiles && attachedFiles.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                  <span>{attachedFiles.length} file(s) selected</span>
                  <button type="button" onClick={removeFiles} className="text-danger hover:text-red-700 font-bold ml-1" title="Remove attachment">
                    &times;
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button className="btn btn-primary flex items-center gap-2" onClick={handleSend} type="button">
                <Send size={16} /> Send Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmail;
